import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  TextField,
  Typography,
  Pagination,
  Stack,
  styled,
  Grid,
  Autocomplete,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BsArrowRightCircle } from "react-icons/bs";
import { FaLocationDot } from "react-icons/fa6";
import homebanner from "@assets/images/Real Estate Banner-07.jpg"; 
const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

// ========== STYLED COMPONENTS ========== //
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "600px",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 80,
  left: 0,
  right: 0,
  textAlign: "center",
  zIndex: 2,
  [theme.breakpoints.down("md")]: {
    bottom: 40,
  },
}));

const AreaCard = styled(Card)(({ theme }) => ({
  maxWidth: "100%",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  transition: "transform 0.3s, box-shadow 0.3s",
  height: "100%",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0,0,0,0.2)",
    "& .explore-text": {
      transform: "translateX(5px)",
    },
  },
}));

// ========== MAIN COMPONENT ========== //
const MarketReport = () => {
  const [areas, setAreas] = useState([]);
  const [page, setPage] = useState(1);
  const [titles, setTitles] = useState([]);
  const navigate = useNavigate();
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await axios.get(
          `${DESCO_API_BASE}/areas/index`
        );
        const titlesArray = response.data.data.map((item) => item.title);
        setTitles(titlesArray);
        setAreas(response.data.data);
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };
    fetchAreas();
  }, []);

  // Pagination logic
  const paginatedAreas = areas.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {/* Hero Banner */}
      <HeroSection>
        <img src={homebanner} alt="Dubai Areas" />

        <SearchBox>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 15,
                textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              Market Reports
            </Typography>
            {/* <Autocomplete
              options={titles}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search areas..."
                  sx={{
                    width: { xs: "90%", sm: "70%", md: "50%" },
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderRadius: "50px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "50px",
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <FaLocationDot
                        style={{ marginRight: 10, color: "#C10E14" }}
                      />
                    ),
                  }}
                />
              )}
            /> */}
          </motion.div>
        </SearchBox>
      </HeroSection>

      {/* Areas Grid */}
      <Container sx={{ py: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 6,
            textAlign: "center",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -10,
              left: "50%",
              transform: "translateX(-50%)",
              width: 80,
              height: 4,
              backgroundColor: "#C10E14",
            },
          }}
        >
          Explore Reports
        </Typography>

        <Grid container spacing={4}>
          {paginatedAreas.map((area, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AreaCard>
                  <CardActionArea
                    // onClick={() => navigate("/areadetail")}
                    sx={{ height: "100%" }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={area.thumbnail}
                        alt={area.title}
                        sx={{
                          transition: "transform 0.5s",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          backgroundColor: "rgba(255,255,255,0.9)",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "16px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <FaLocationDot color="#C10E14" />
                        Dubai
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        gutterBottom
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: "#333",
                          mb: 2,
                          minHeight: "64px",
                        }}
                      >
                        {area.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.6 }}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ mb: 2, lineHeight: 1.6 }}
                        >
                          {area.description.length > 100
                            ? `${area.description.slice(0, 150)}...`
                            : area.description}
                        </Typography>
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "#C10E14",
                          fontWeight: 600,
                          mt: 2,
                        }}
                      >
                        <span className="explore-text">Explore Area</span>
                        <BsArrowRightCircle
                          style={{
                            marginLeft: 8,
                            transition: "transform 0.3s",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </AreaCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {areas.length > itemsPerPage && (
          <Stack spacing={2} sx={{ mt: 6, alignItems: "center" }}>
            <Pagination
              count={Math.ceil(areas.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 600,
                  "&.Mui-selected": {
                    backgroundColor: "#C10E14",
                    color: "white",
                  },
                },
              }}
            />
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default MarketReport;
